import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Fingerprint, Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { isWebAuthnSupported, enrollPasskey, listPasskeys, deletePasskey } from '../../services/webauthn';

/**
 * Passkey enrollment + management card.
 * Shown on the Profile page for any logged-in user whose browser supports
 * WebAuthn. Lets the user enable Face ID / Touch ID / Windows Hello / etc.
 * so they can log in without email OTP on subsequent visits.
 */
export function PasskeyCard() {
  const [supported] = useState(isWebAuthnSupported());
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    if (!supported) { setLoading(false); return; }
    try {
      const rows = await listPasskeys();
      setCreds(rows);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [supported]);

  useEffect(() => { load(); }, [load]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const deviceName = navigator.platform || 'This device';
      await enrollPasskey(deviceName);
      toast.success('Face ID / Passkey enabled on this device');
      await load();
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        toast.error('Cancelled');
      } else if (e.name === 'InvalidStateError') {
        toast.info('This device is already enrolled.');
      } else {
        toast.error(e.response?.data?.detail || e.message || 'Could not enable passkey');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this passkey? You will need to enable it again on this device.')) return;
    setRemovingId(id);
    try {
      await deletePasskey(id);
      toast.success('Passkey removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove passkey');
    } finally {
      setRemovingId(null);
    }
  };

  if (!supported) return null;

  return (
    <Card className="shadow-lg border-0" data-testid="passkey-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-gk-navy">
          <Fingerprint className="w-5 h-5 text-gk-teal" />
          Face ID / Passkey Login
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500 leading-snug">
          Enable biometric login on this device. Uses your device&apos;s fingerprint,
          face recognition, or PIN — no password or OTP needed next time.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-4 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {creds.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No passkeys enrolled yet.</p>
            ) : (
              <div className="space-y-2">
                {creds.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`passkey-row-${c.id}`}
                    className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {c.device_name || 'Device'}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Added {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                        {c.last_used_at && <> · Last used {new Date(c.last_used_at).toLocaleDateString()}</>}
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDelete(c.id)}
                      disabled={removingId === c.id}
                      className="text-red-600 hover:bg-red-50 h-8 px-2"
                      data-testid={`passkey-delete-${c.id}`}
                    >
                      {removingId === c.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              onClick={handleEnroll}
              disabled={enrolling}
              className="w-full bg-gk-teal hover:bg-gk-teal-hover text-white h-10"
              data-testid="passkey-enroll-button"
            >
              {enrolling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Waiting for device…</>
              ) : (
                <><Fingerprint className="w-4 h-4 mr-2" /> {creds.length === 0 ? 'Enable Face ID / Passkey' : 'Enroll another device'}</>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
